import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router';
import { AgGridReact } from 'ag-grid-react';
import Moment from 'react-moment';
import AppContext from 'AppContext';
import GrpButton from 'Library/GrpButton/GrpButton';
import GrpSpinner from 'Components/GrpSpinner/GrpSpinner';
import {
  getTargetProfiles,
  getTargetTypes,
  getTargetProfileDistributionTargetsFtp,
  getTargetsFtps,
  getTargetsScopes,
  deleteTargetProfile,
  duplicateTargetProfile,
  uploadDistributionFile,
} from 'Services/distributionApi';
import classes from './TargetProfilesList.module.scss';

const TargetProfilesList = () => {
  const context = useContext(AppContext);
  const navigate = useNavigate();
  const gridRef = useRef(null);
  const fileInputRef = useRef(null);

  const [rowData, setRowData] = useState([]);
  const [targetTypes, setTargetTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, profile: null });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      const [profilesResult, typesResult] = await Promise.all([
        getTargetProfiles(),
        getTargetTypes(),
        getTargetProfileDistributionTargetsFtp(),
        getTargetsFtps(),
        getTargetsScopes(),
      ]);

      const profiles = profilesResult.value || profilesResult.Content || profilesResult || [];
      const types = typesResult.value || typesResult.Content || typesResult || [];

      setRowData(Array.isArray(profiles) ? profiles : []);
      setTargetTypes(Array.isArray(types) ? types : []);
    } catch (err) {
      context.handlers.setToast({ type: 'error', body: <>{err.human || 'Failed to load target profiles'}</> });
    } finally {
      setLoading(false);
    }
  }, [context.handlers]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Close context menu on outside click
  useEffect(() => {
    const handleClick = () => setContextMenu((prev) => ({ ...prev, visible: false }));
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const getTypeName = useCallback((targetTypeId) => {
    const type = targetTypes.find((t) => t.TargetTypeId === targetTypeId);
    return type?.TargetTypeName || '';
  }, [targetTypes]);

  const columnDefs = [
    {
      headerName: 'Name',
      field: 'TargetProfileName',
      filter: 'agTextColumnFilter',
      flex: 1,
      cellRenderer: (params) => (
        <span
          className={classes.navLink}
          onClick={() => navigate(`/distribution/profile-edit/${params.data.TargetProfileId}`)}
        >
          {params.value}
        </span>
      ),
    },
    {
      headerName: 'Type',
      field: 'TargetTypeId',
      filter: 'agTextColumnFilter',
      width: 180,
      valueGetter: (params) => getTypeName(params.data.TargetTypeId),
    },
    {
      headerName: 'Status',
      field: 'IsActive',
      width: 120,
      filter: 'agTextColumnFilter',
      valueGetter: (params) => (params.data.IsActive ? 'Active' : 'Inactive'),
    },
    {
      headerName: 'Last Activity',
      field: 'UpdatedDateTime',
      width: 200,
      filter: 'agDateColumnFilter',
      cellRenderer: (params) => (
        params.value
          ? <Moment format="YYYY/MM/DD hh:mm A">{params.value}</Moment>
          : ''
      ),
    },
    {
      headerName: 'Actions',
      width: 90,
      sortable: false,
      filter: false,
      cellRenderer: (params) => (
        <GrpButton
          size="Small"
          icon={['fas', 'ellipsis-vertical']}
          iconSide="Round"
          onClick={(e) => {
            e.stopPropagation();
            setContextMenu({
              visible: true,
              x: e.clientX,
              y: e.clientY,
              profile: params.data,
            });
          }}
        />
      ),
    },
  ];

  const handlePreview = () => {
    if (contextMenu.profile) {
      navigate(`/distribution/profile-preview/${contextMenu.profile.TargetProfileId}`);
    }
    setContextMenu((prev) => ({ ...prev, visible: false }));
  };

  const handleEdit = () => {
    if (contextMenu.profile) {
      navigate(`/distribution/profile-edit/${contextMenu.profile.TargetProfileId}`);
    }
    setContextMenu((prev) => ({ ...prev, visible: false }));
  };

  const handleDelete = async () => {
    const profile = contextMenu.profile;
    setContextMenu((prev) => ({ ...prev, visible: false }));
    if (!profile) return;

    try {
      setLoading(true);
      await deleteTargetProfile(profile.TargetProfileId);
      context.handlers.setToast({
        type: 'success',
        body: <>{`Target Profile "${profile.TargetProfileName}" has been deleted!`}</>,
      });
      await loadData();
    } catch (err) {
      context.handlers.setToast({ type: 'error', body: <>{err.human || 'Failed to delete profile'}</> });
      setLoading(false);
    }
  };

  const handleDuplicate = async () => {
    const profile = contextMenu.profile;
    setContextMenu((prev) => ({ ...prev, visible: false }));
    if (!profile) return;

    try {
      setLoading(true);
      const newProfile = await duplicateTargetProfile(profile);
      const name = newProfile?.TargetProfileName || profile.TargetProfileName;
      context.handlers.setToast({
        type: 'success',
        body: <>{`Target Profile "${name}" has been created!`}</>,
      });
      await loadData();
    } catch (err) {
      context.handlers.setToast({ type: 'error', body: <>{err.human || 'Failed to duplicate profile'}</> });
      setLoading(false);
    }
  };

  const handleCreate = () => {
    navigate('/distribution/profile-edit/');
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setLoading(true);
      const res = await uploadDistributionFile(file, 'Target Profile');
      await new Promise((resolve) => setTimeout(resolve, 5000));

      if (res?.Success === false) {
        context.handlers.setToast({ type: 'error', body: <>{res.ExceptionMessage || 'Upload failed'}</> });
      } else {
        context.handlers.setToast({ type: 'success', body: <>File uploaded successfully. Refresh the page.</> });
      }
      await loadData();
    } catch (err) {
      context.handlers.setToast({ type: 'error', body: <>{err.human || 'Failed to upload file'}</> });
      setLoading(false);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (loading) {
    return <GrpSpinner />;
  }

  return (
    <div className={classes.container}>
      <div className={classes.toolbar}>
        <span className={classes.spacer} />
        <GrpButton
          size="Medium"
          type="Primary"
          text="Create Target Profile"
          onClick={handleCreate}
        />
        <GrpButton
          size="Medium"
          type="Secondary"
          text="Import Target Profile"
          onClick={handleImportClick}
        />
        <input
          ref={fileInputRef}
          type="file"
          accept=".xls,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </div>

      <div className={`ag-theme-reborn ${classes.gridWrapper}`}>
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={{
            sortable: true,
            resizable: true,
            filter: true,
          }}
          pagination={true}
          paginationPageSize={50}
          domLayout="autoHeight"
        />
      </div>

      {contextMenu.visible && (
        <div
          className={classes.contextMenu}
          style={{ top: contextMenu.y, left: contextMenu.x }}
        >
          <div className={classes.menuItem} onClick={handlePreview}>Preview</div>
          <div className={classes.menuItem} onClick={handleEdit}>Edit</div>
          <div className={classes.menuItem} onClick={handleDelete}>Delete</div>
          <div className={classes.menuItem} onClick={handleDuplicate}>Duplicate</div>
        </div>
      )}
    </div>
  );
};

export default TargetProfilesList;
